import React, { useState } from 'react'
import { Box, Button, CardContent, Chip, Dialog, Typography } from '@mui/material'

import { Cards, ColoredChip, EmptyPlaceholder } from 'src/legacy/components/mui'
import { FormatDateTime } from 'src/date'
import HeightAwareIframe from 'src/legacy/components/HeightAwareIframe'
import ScrollContainer from 'src/legacy/components/ScrollContainer'
import { ExpandableCard, Loader } from 'src/designSystem'
import SendEmailForm from 'src/legacy/components/SendEmailForm/SendEmailForm'
import { useGetEmailsQuery } from './getEmails.generated'

// A soft bounce may still deliver; a hard bounce never will.
const STATUS_CHIP_COLOR: Record<string, 'warning' | 'error'> = {
  BOUNCED: 'warning',
  HARD_BOUNCED: 'error',
}

const ParticipantEmails = ({ uid }: { uid: string }) => {
  const { data, loading, refetch } = useGetEmailsQuery({ variables: { uid } })
  const [sendEmailOpen, setSendEmailOpen] = useState(false)
  const noEntriesFound = data?.emails.length === 0

  return (
    <ScrollContainer>
      <Cards>
        {loading && <Loader size="large" />}
        {noEntriesFound && <EmptyPlaceholder />}
        {data?.emails.map((email) => (
          <ExpandableCard
            key={email.emailID}
            title={email.subject as string}
            subheader={
              <Box display="flex" justifyContent="space-between">
                <span>
                  {email.from && (
                    <>
                      From: {email.from}
                      <br />
                    </>
                  )}
                  To: {email.to}
                </span>
                <Box display="flex" flexDirection="column" alignItems="flex-end">
                  <ColoredChip
                    color={STATUS_CHIP_COLOR[email.status] ?? null}
                    label={
                      <>
                        {email.status} <FormatDateTime value={email.statusChangedAt} />
                      </>
                    }
                    size="small"
                  />
                  {email.status !== 'SENDING' && (
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                      Sent <FormatDateTime value={email.createdAt} />
                    </Typography>
                  )}
                </Box>
              </Box>
            }
          >
            <CardContent>
              {email.htmlBody ? (
                <HeightAwareIframe html={email.htmlBody} />
              ) : (
                // Body presence and delivery status are independent signals: a Postmark
                // email is still being synced while SENDING; anything else with no body
                // is one we won't get content for.
                <Box alignItems="center" display="flex" justifyContent="center">
                  <Chip label={email.status === 'SENDING' ? 'Sending' : 'No preview available'} />
                </Box>
              )}
            </CardContent>
          </ExpandableCard>
        ))}
      </Cards>
      <Box display="flex" justifyContent="flex-end" mt={1}>
        <Button variant="contained" color="primary" onClick={() => setSendEmailOpen(true)}>
          Send Email
        </Button>
      </Box>
      <Dialog fullWidth maxWidth="sm" onClose={() => setSendEmailOpen(false)} open={sendEmailOpen}>
        <SendEmailForm close={() => setSendEmailOpen(false)} onSuccess={() => refetch()} uids={[uid]} />
      </Dialog>
    </ScrollContainer>
  )
}

export default ParticipantEmails
